/**
 * **The guest screen** (the two screens; `GuestScreen.dc.html`): the 15.6" touch display facing
 * the volunteer, opened by the station's own button as a window on the second display. Paired by
 * the station's session — the same enrollment as /booth, never a sign-in. Every state is put up by
 * the operator through the API, so either window can reload and read it back:
 *
 *   WELCOME — and, while nobody is being checked in, the screensaver: the welcome, where people
 *   are needed, the sponsors' thanks and the day's count, twenty seconds each, snapping back to the
 *   person the moment a check-in starts · CONFIRM ("Is this you?", YES and NO — NO says the desk
 *   will find them) · WAIVER (the year's text and I AGREE, with a finger box on a touch screen; a
 *   person who signed this year skips it, "signed Sep 8"; a teen's holds for the parent's line,
 *   DRAFT) · TAG ("Your tag is printing") · THANK YOU. A practice day says PRACTICE DAY.
 *
 * It never shows another person's name, a phone or an address — the person's own first name, the
 * area, the time, and the area chair's first name. Big type, the mark, no inside words.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { MARK, formatDate, formatTime } from '@cider/ui';
import { api, isRefused, sentence } from '../../shell/api.js';
import type { Me } from '../../shell/session.js';

interface GuestView {
  state:
    | 'welcome'
    | 'confirm'
    | 'notme'
    | 'hold'
    | 'walkup'
    | 'why'
    | 'whyfamily'
    | 'household'
    | 'wrongday'
    | 'waiver'
    | 'tag'
    | 'thanks'
    | 'visitor';
  firstName: string | null;
  area: string | null;
  startsAt: string | null;
  endsAt: string | null;
  chair: string | null;
  confirmed: boolean;
  signed: boolean;
  waiver: { name: string; version: string; text: string | null } | null;
  /** The visitor waiver's words (ADR 0032), DRAFT until the festival gives its own. */
  visitorWaiver?: string | null;
  practice?: boolean;
  demo?: boolean;
  signedOn?: string | null;
  teen?: boolean;
  /** A teen in a group: the group's leader, the adult present (Rebuild 29, state 9). */
  leader?: string | null;
  /** Which family? (G05): by the family's own name only. */
  families?: { familyId: string; label: string }[];
  /** The household on its own guest screen (G06): its own members, after its own QR. */
  household?: {
    name: string;
    members: { personId: string; firstName: string; working: boolean; age: number | null }[];
    here: string[];
  };
}

/** What the screensaver shows between people (Oct 6): read once a minute, never a control. */
interface Idle {
  needed: { area: string; startsAt: string; endsAt: string; short: number }[];
  count: number;
}
const SLIDES = ['welcome', 'needed', 'sponsors', 'count'] as const;

const hours = (a: string | null, b: string | null): string =>
  a && b ? `${formatTime(a)} – ${formatTime(b)}` : '';

export function BoothGuest({ me }: { me: Me | null }): ReactNode {
  const paired = me?.signedIn && me.kind === 'device' && me.device?.kind === 'booth';
  const [view, setView] = useState<GuestView | null>(null);
  const [trouble, setTrouble] = useState<string | null>(null);
  const [tapped, setTapped] = useState(document.fullscreenElement !== null);

  const read = useCallback((): void => {
    api<GuestView>('GET', '/volunteers/booth/guest?from=guest')
      .then((v) => {
        setView(v);
        setTrouble(null);
      })
      .catch((e: unknown) => {
        // With no network (7g) the guest screen says nothing about it: it keeps what it shows.
        if (isRefused(e) && e.refusal.status === 0) return;
        setTrouble(sentence(e));
      });
  }, []);
  // The operator drives this screen; it reads every second and a half (one small read, in memory).
  useEffect(() => {
    if (!paired) return;
    read();
    const t = setInterval(read, 1_500);
    return () => {
      clearInterval(t);
    };
  }, [paired, read]);

  // The screensaver's two reads: the day once a minute, the sponsors every ten.
  const [idle, setIdle] = useState<Idle | null>(null);
  const [sponsors, setSponsors] = useState<string[]>([]);
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (!paired) return;
    const day = (): void => {
      api<Idle>('GET', '/volunteers/booth/guest/idle')
        .then(setIdle)
        .catch(() => undefined);
    };
    const thanks = (): void => {
      api<{ sponsors: { names: { name: string }[] } | null }>('GET', '/public/site')
        .then((r) => {
          setSponsors((r.sponsors?.names ?? []).map((n) => n.name).slice(0, 12));
        })
        .catch(() => undefined);
    };
    day();
    thanks();
    const a = setInterval(day, 60_000);
    const b = setInterval(thanks, 600_000);
    return () => {
      clearInterval(a);
      clearInterval(b);
    };
  }, [paired]);
  const idleNow = (view?.state ?? 'welcome') === 'welcome';
  useEffect(() => {
    if (!idleNow) {
      setSlide(0);
      return;
    }
    const t = setInterval(() => {
      setSlide((n) => (n + 1) % SLIDES.length);
    }, 20_000);
    return () => {
      clearInterval(t);
    };
  }, [idleNow]);

  if (!paired)
    return (
      <div className="guest guest--quiet" data-screen="booth-guest" data-guest="unpaired">
        <img className="guest__mark" src={MARK} alt="Apple Fest" />
        <h1 className="guest__big">This screen isn’t set up</h1>
        <p className="guest__line">Pair it at the counter.</p>
      </div>
    );

  // The first tap puts the window full screen; the browser keeps it from then on.
  if (!tapped)
    return (
      <button
        type="button"
        className="guest guest--quiet guest__tap"
        data-screen="booth-guest"
        data-guest="tap"
        onClick={() => {
          void document.documentElement.requestFullscreen().catch(() => undefined);
          setTapped(true);
        }}
      >
        <img className="guest__mark" src={MARK} alt="Apple Fest" />
        <span className="guest__big">Tap once to begin</span>
      </button>
    );

  const v = view;
  const state = v?.state ?? 'welcome';
  return (
    <div className="guest" data-screen="booth-guest" data-guest={state}>
      <img className="guest__mark" src={MARK} alt="Apple Fest" />
      {v?.practice ? (
        <p className="guest__practice" data-testid="guest-practice">
          PRACTICE DAY{v.demo ? ' · DEMO YEAR' : ''}
        </p>
      ) : null}
      {trouble ? <p className="guest__line">One moment — the counter is reconnecting.</p> : null}
      {state === 'welcome' || !v ? (
        <div data-testid="guest-slide" data-slide={SLIDES[slide]}>
          {SLIDES[slide] === 'needed' && idle?.needed.length ? (
            <>
              {/* Rebuild 32 (ADR 0032): the third idle card — where we're short, asked at the desk. */}
              <h1 className="guest__big">Right now we could use a hand</h1>
              {idle.needed.map((n) => (
                <p className="guest__line guest__short" key={`${n.area}${n.startsAt}`}>
                  <b>{n.area}</b> <span className="guest__shortN">{n.short}</span>
                </p>
              ))}
              <p className="guest__line">Ask at the desk.</p>
            </>
          ) : SLIDES[slide] === 'sponsors' && sponsors.length ? (
            <>
              <h1 className="guest__big">Thank you to our sponsors</h1>
              <p className="guest__line">{sponsors.join(' · ')}</p>
            </>
          ) : SLIDES[slide] === 'count' && idle ? (
            <>
              <h1 className="guest__big">{idle.count}</h1>
              <p className="guest__line">volunteers checked in today — thank you</p>
            </>
          ) : (
            <>
              <h1 className="guest__big">Welcome</h1>
              <p className="guest__line">St. Lawrence Apple Fest · check in at the counter</p>
            </>
          )}
        </div>
      ) : state === 'visitor' ? (
        <VisitorWaiver firstName={v.firstName} text={v.visitorWaiver ?? null} />
      ) : state === 'hold' ? (
        <>
          <h1 className="guest__big">One moment, please</h1>
          <p className="guest__line">The counter will be right with you.</p>
        </>
      ) : state === 'notme' ? (
        <>
          <h1 className="guest__big">One moment</h1>
          <p className="guest__line">The desk will find you.</p>
        </>
      ) : state === 'confirm' ? (
        <>
          <h1 className="guest__big">Is this you?</h1>
          <p className="guest__name">{v.firstName}</p>
          <p className="guest__line">
            {[v.area, hours(v.startsAt, v.endsAt)].filter(Boolean).join(' · ')}
          </p>
          {v.confirmed ? (
            <p className="guest__line">
              Thank you.
              {v.signedOn ? ` This year’s waiver — signed ${formatDate(v.signedOn)}.` : ''}
            </p>
          ) : (
            <div className="guest__answers">
              <button
                type="button"
                className="guest__yes"
                onClick={() => {
                  void api('POST', '/volunteers/booth/guest/yes')
                    .then(read)
                    .catch(() => undefined);
                }}
              >
                Yes
              </button>
              <button
                type="button"
                className="guest__yes guest__no"
                onClick={() => {
                  void api('POST', '/volunteers/booth/guest/no')
                    .then(read)
                    .catch(() => undefined);
                }}
              >
                No
              </button>
            </div>
          )}
        </>
      ) : state === 'wrongday' ? (
        <div data-testid="guest-wrongday">
          <h1 className="guest__big">
            {v.firstName}, you’re on {v.startsAt ? formatDate(v.startsAt) : 'another day'}
          </h1>
          <p className="guest__line">
            {v.area ? `${v.area} · ${hours(v.startsAt, v.endsAt)}. ` : ''}Want to help now? The
            counter will find you a spot.
          </p>
        </div>
      ) : state === 'household' && v.household ? (
        <HouseholdHere household={v.household} onDone={read} />
      ) : state === 'whyfamily' ? (
        <WhichFamily view={v} onDone={read} />
      ) : state === 'why' ? (
        <WhyHere view={v} onDone={read} />
      ) : state === 'walkup' ? (
        <WalkUpDetails view={v} onDone={read} />
      ) : state === 'waiver' ? (
        v.signed ? (
          <>
            <h1 className="guest__big">Thank you, {v.firstName}</h1>
            <p className="guest__line">Signed. The counter has it.</p>
          </>
        ) : v.teen && !v.leader ? (
          <TeenWaiver view={v} onSigned={read} />
        ) : (
          <Waiver view={v} onSigned={read} />
        )
      ) : state === 'tag' ? (
        <>
          <h1 className="guest__big">Your tag is printing</h1>
          <p className="guest__line">
            {v.area ? `Head to the ${v.area}` : 'Head to your area'}
            {v.chair ? ` · your chair is ${v.chair}` : ''}
          </p>
        </>
      ) : (
        <>
          <h1 className="guest__big">Thank you{v.firstName ? `, ${v.firstName}` : ''}</h1>
          <p className="guest__line">Thank you for working Apple Fest.</p>
        </>
      )}
    </div>
  );
}

/**
 * **The household — tap who is here** (Rebuild 29, G06): after the family page's QR, the
 * household's own members, first names only; each taps themselves in or out, and the desk's
 * household card follows. A household's own members on its own screen (James, Oct 9).
 */
function HouseholdHere({
  household,
  onDone,
}: {
  household: NonNullable<GuestView['household']>;
  onDone: () => void;
}): ReactNode {
  const [trouble, setTrouble] = useState<string | null>(null);
  const here = new Set(household.here);
  const names = household.members.filter((m) => here.has(m.personId)).map((m) => m.firstName);
  return (
    <div className="guest__waiver" data-testid="guest-household">
      <h1 className="guest__big">{household.name}</h1>
      <p className="guest__line">Tap who is here.</p>
      {household.members.map((m) => (
        <button
          type="button"
          key={m.personId}
          className={here.has(m.personId) ? 'guest__yes' : 'guest__yes guest__no'}
          aria-pressed={here.has(m.personId)}
          onClick={() => {
            api('POST', '/volunteers/booth/guest/here', {
              personId: m.personId,
              here: !here.has(m.personId),
            })
              .then(() => {
                onDone();
              })
              .catch((e: unknown) => {
                setTrouble(sentence(e));
              });
          }}
        >
          {here.has(m.personId) ? '✓ ' : ''}
          {m.firstName}
          {m.age !== null ? `, ${String(m.age)}` : ''} · {m.working ? 'working today' : 'not today'}
        </button>
      ))}
      <p className="guest__line" role="status">
        {names.length === 0
          ? 'Nobody tapped yet.'
          : names.length === 1
            ? `${names[0] ?? ''} is here.`
            : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''} are here.`}
      </p>
      {trouble ? (
        <p className="guest__line" role="alert">
          {trouble}
        </p>
      ) : null}
    </div>
  );
}

/**
 * **Which family?** (Rebuild 29, G05): "so your hours count toward your family's commitment" — the
 * families on offer by their own name, never a person's name or an address, and "not one of these".
 */
function WhichFamily({ view, onDone }: { view: GuestView; onDone: () => void }): ReactNode {
  const [trouble, setTrouble] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pick = (familyId: string | null): void => {
    setBusy(true);
    api('POST', '/volunteers/booth/guest/family', { familyId })
      .then(() => {
        onDone();
      })
      .catch((e: unknown) => {
        setTrouble(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <div className="guest__waiver" data-testid="guest-whyfamily">
      <h1 className="guest__big">Which family?</h1>
      <p className="guest__line">So your hours count toward your family’s commitment.</p>
      {(view.families ?? []).map((f) => (
        <button
          type="button"
          key={f.familyId}
          className="guest__yes"
          disabled={busy}
          onClick={() => {
            pick(f.familyId);
          }}
        >
          {f.label}
        </button>
      ))}
      <button
        type="button"
        className="guest__yes guest__no"
        disabled={busy}
        onClick={() => {
          pick(null);
        }}
      >
        Not one of these — the counter will help
      </button>
      {trouble ? (
        <p className="guest__line" role="alert">
          {trouble}
        </p>
      ) : null}
    </div>
  );
}

/**
 * **Why are you here helping today?** (Rebuild 29, G04): asked once a year, answered by the
 * person — the same four answers the desk has, and the same record.
 */
function WhyHere({ view, onDone }: { view: GuestView; onDone: () => void }): ReactNode {
  const [trouble, setTrouble] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const OPTIONS: [string, string, string][] = [
    ['parishioner', 'Parishioner', 'I belong to Saint Lawrence'],
    ['community', 'Community member', 'I live nearby or came to help'],
    ['school_family', 'School family', 'For our family’s commitment'],
    ['service_hours', 'Service hours', 'For school, NHS, confirmation or another program'],
  ];
  return (
    <div className="guest__waiver" data-testid="guest-why">
      <p className="guest__line">Hi {view.firstName}</p>
      <h1 className="guest__big">Why are you here helping today?</h1>
      {OPTIONS.map(([reason, label, line]) => (
        <button
          type="button"
          key={reason}
          className="guest__yes"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            api('POST', '/volunteers/booth/guest/why', { reason })
              .then(() => {
                onDone();
              })
              .catch((e: unknown) => {
                setTrouble(sentence(e));
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          {label} — {line}
        </button>
      ))}
      {trouble ? (
        <p className="guest__line" role="alert">
          {trouble}
        </p>
      ) : null}
    </div>
  );
}

/**
 * **A walk-up's own details** (Rebuild 29, G11): "Welcome, Renee" — their email, mobile and an
 * emergency contact in large fields, then the waiver. The counter helps the next person meanwhile.
 */
function WalkUpDetails({ view, onDone }: { view: GuestView; onDone: () => void }): ReactNode {
  const [f, setF] = useState({ email: '', mobile: '', ecName: '', ecPhone: '' });
  const [trouble, setTrouble] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fields: [keyof typeof f, string, string][] = [
    ['email', 'Email', 'you@example.com'],
    ['mobile', 'Mobile', ''],
    ['ecName', 'Emergency contact', 'Their name'],
    ['ecPhone', 'Their phone', ''],
  ];
  return (
    <div className="guest__waiver" data-testid="guest-walkup">
      <h1 className="guest__big">Welcome, {view.firstName}</h1>
      <p className="guest__line">
        {view.area ? `${view.area} · ${hours(view.startsAt, view.endsAt)}. ` : ''}A few details so
        we can reach you.
      </p>
      {fields.map(([k, label, hint]) => (
        <label className="guest__line" key={k}>
          {label}
          <input
            className="guest__input"
            aria-label={label}
            placeholder={hint}
            inputMode={k === 'email' ? 'email' : k === 'ecName' ? 'text' : 'tel'}
            value={f[k]}
            onChange={(e) => {
              setF({ ...f, [k]: e.target.value });
            }}
          />
        </label>
      ))}
      {trouble ? (
        <p className="guest__line" role="alert">
          {trouble}
        </p>
      ) : null}
      <button
        type="button"
        className="guest__yes"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          const val = (x: string): string | null => (x.trim() ? x.trim() : null);
          api('POST', '/volunteers/booth/guest/details', {
            email: val(f.email),
            mobile: val(f.mobile),
            ecName: val(f.ecName),
            ecPhone: val(f.ecPhone),
          })
            .then(() => {
              onDone();
            })
            .catch((e: unknown) => {
              setTrouble(sentence(e));
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        {view.signed ? 'Next' : 'Next — the waiver'}
      </button>
    </div>
  );
}

/**
 * **The teen rule** (Rebuild 29, G9): "Bao, a parent signs for you." The parent here says so,
 * gives their name, then reads and signs — the record names them as the parent who signed.
 */
function TeenWaiver({ view, onSigned }: { view: GuestView; onSigned: () => void }): ReactNode {
  const [parentHere, setParentHere] = useState(false);
  const [name, setName] = useState('');
  const [ready, setReady] = useState(false);
  if (ready) return <Waiver view={view} onSigned={onSigned} parentName={name.trim()} />;
  return (
    <>
      <h1 className="guest__big" data-testid="guest-teen">
        {view.firstName}, a parent signs for you.
      </h1>
      {parentHere ? (
        <div className="guest__waiver">
          <p className="guest__line">Your name, as {view.firstName}’s parent:</p>
          <input
            className="guest__input"
            aria-label="The parent’s name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <button
            type="button"
            className="guest__yes"
            disabled={name.trim().split(/\s+/).length < 2}
            onClick={() => {
              setReady(true);
            }}
          >
            Next — the waiver
          </button>
        </div>
      ) : (
        <>
          <p className="guest__line">
            A parent here reads {view.firstName}’s waiver and signs it. Wording pending the parish’s
            advisor.
          </p>
          <button
            type="button"
            className="guest__yes"
            onClick={() => {
              setParentHere(true);
            }}
          >
            I’m {view.firstName}’s parent — continue
          </button>
        </>
      )}
    </>
  );
}

/**
 * The year's waiver, as drawn (Oct 6): the text, read to the end, and I AGREE — with a finger box
 * where the screen is touch. The person's own name is the typed line.
 */
function Waiver({
  view,
  onSigned,
  parentName,
}: {
  view: GuestView;
  onSigned: () => void;
  /** A teen's waiver: the parent who signs it. */
  parentName?: string;
}): ReactNode {
  const [read, setRead] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const [trouble, setTrouble] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pad = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const touch = window.matchMedia('(pointer: coarse)').matches;

  const at = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const c = e.currentTarget;
    const r = c.getBoundingClientRect();
    return [
      ((e.clientX - r.left) / r.width) * c.width,
      ((e.clientY - r.top) / r.height) * c.height,
    ];
  };
  return (
    <div className="guest__waiver">
      <h1 className="guest__big">This year’s waiver</h1>
      {parentName ? (
        <p className="guest__line">
          For {view.firstName} · signed by {parentName}, as their parent
        </p>
      ) : view.teen && view.leader ? (
        <p className="guest__line" data-testid="guest-leader">
          {view.leader}, your group’s leader, is the adult present.
        </p>
      ) : null}
      <div
        className="guest__text"
        data-testid="guest-waiver-text"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setRead(true);
        }}
        ref={(el) => {
          // A short waiver that fits without scrolling has been read to the end already.
          if (el && el.scrollHeight <= el.clientHeight + 8 && !read) setRead(true);
        }}
      >
        {view.waiver?.text ?? 'Read the waiver on the paper at the counter.'}
      </div>
      {touch ? (
        <canvas
          ref={pad}
          className="guest__pad"
          data-testid="guest-pad"
          width={900}
          height={240}
          aria-label="Sign here with your finger or the pen"
          onPointerDown={(e) => {
            drawing.current = true;
            const ctx = e.currentTarget.getContext('2d');
            const [x, y] = at(e);
            ctx?.beginPath();
            ctx?.moveTo(x, y);
          }}
          onPointerMove={(e) => {
            if (!drawing.current) return;
            const ctx = e.currentTarget.getContext('2d');
            if (!ctx) return;
            const [x, y] = at(e);
            ctx.lineWidth = 4;
            ctx.lineCap = 'round';
            ctx.lineTo(x, y);
            ctx.stroke();
            setDrawn(true);
          }}
          onPointerUp={() => {
            drawing.current = false;
          }}
        />
      ) : null}
      {trouble ? <p className="guest__line">{trouble}</p> : null}
      <button
        type="button"
        className="guest__yes"
        disabled={busy || !read || (touch && !drawn)}
        onClick={() => {
          setBusy(true);
          setTrouble(null);
          api('POST', '/volunteers/booth/guest/sign', {
            agree: true,
            touch,
            ...(parentName ? { parentName } : {}),
            ...(touch ? { signature: pad.current?.toDataURL('image/png') ?? '' } : {}),
          })
            .then(onSigned)
            .catch((e: unknown) => {
              setTrouble(sentence(e));
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        {!read ? 'Read to the end first' : 'I agree'}
      </button>
    </div>
  );
}

/**
 * **The visitor waiver** (ADR 0032; GuestScreen.dc.html `visitor`): the visitor reads the visitor
 * version, types their own name and taps I agree — once a festival. Held on the visitor record with
 * in and out, and nothing else. The words are DRAFT.
 */
function VisitorWaiver({
  firstName,
  text,
}: {
  firstName: string | null;
  text: string | null;
}): ReactNode {
  const [typed, setTyped] = useState('');
  const [trouble, setTrouble] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <h1 className="guest__big">{`The visitor waiver${firstName ? ` · ${firstName}` : ''}`}</h1>
      <p className="guest__line">DRAFT text · read it, then type your name and tap I agree.</p>
      <div className="guest__waiver" data-testid="guest-visitor-waiver">
        {(text ?? '').split('\n').map((line) => (
          <p className="guest__line" key={line}>
            {line}
          </p>
        ))}
      </div>
      <input
        className="guest__input"
        aria-label="Your name"
        value={typed}
        onChange={(e) => {
          setTyped(e.target.value);
        }}
      />
      {trouble ? <p className="guest__line">{trouble}</p> : null}
      <button
        type="button"
        className="guest__yes"
        disabled={busy || !typed.trim()}
        onClick={() => {
          setBusy(true);
          api('POST', '/volunteers/booth/guest/visitor-sign', { typedName: typed, agree: true })
            .catch((e: unknown) => {
              setTrouble(sentence(e));
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        I AGREE
      </button>
    </>
  );
}
